/**
 * Request Queue Utility
 * Stores failed requests in IndexedDB and retries them when online
 */

const DB_NAME = 'jcaapl-request-queue';
const DB_VERSION = 1;
const STORE_NAME = 'failed-requests';

let db = null;

// Initialize IndexedDB
async function initDB() {
  if (db) return db;

  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => {
      db = request.result;
      resolve(db);
    };

    request.onupgradeneeded = (event) => {
      const database = event.target.result;
      if (!database.objectStoreNames.contains(STORE_NAME)) {
        const objectStore = database.createObjectStore(STORE_NAME, { keyPath: 'id', autoIncrement: true });
        objectStore.createIndex('timestamp', 'timestamp', { unique: false });
        objectStore.createIndex('url', 'url', { unique: false });
      }
    };
  });
}

/**
 * Add a failed request to the queue
 * @param {Object} requestData - The request configuration
 * @param {string} requestData.url - Request URL
 * @param {string} requestData.method - HTTP method
 * @param {Object} requestData.data - Request payload
 * @param {Object} requestData.headers - Request headers
 */
export async function queueRequest(requestData) {
  try {
    const database = await initDB();
    const transaction = database.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);

    const queuedRequest = {
      url: requestData.url,
      method: requestData.method || 'POST',
      data: requestData.data,
      headers: requestData.headers || {},
      timestamp: Date.now(),
      retries: 0,
    };

    await store.add(queuedRequest);
    return true;
  } catch (error) {
    console.error('Error queueing request:', error);
    return false;
  }
}

/**
 * Get all queued requests
 * @returns {Promise<Array>} Array of queued requests
 */
export async function getQueuedRequests() {
  try {
    const database = await initDB();
    const transaction = database.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const index = store.index('timestamp');

    return new Promise((resolve, reject) => {
      const request = index.getAll();
      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Error getting queued requests:', error);
    return [];
  }
}

/**
 * Remove a request from the queue
 * @param {number} id - Request ID
 */
export async function removeQueuedRequest(id) {
  try {
    const database = await initDB();
    const transaction = database.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    await store.delete(id);
    return true;
  } catch (error) {
    console.error('Error removing queued request:', error);
    return false;
  }
}

/**
 * Clear all queued requests
 */
export async function clearQueue() {
  try {
    const database = await initDB();
    const transaction = database.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    await store.clear();
    return true;
  } catch (error) {
    console.error('Error clearing queue:', error);
    return false;
  }
}

/**
 * Retry all queued requests
 * @param {Function} apiCall - Function to make API call
 * @returns {Promise<Object>} Results of retry attempts
 */
export async function retryQueuedRequests(apiCall) {
  const queued = await getQueuedRequests();
  const results = {
    success: 0,
    failed: 0,
    errors: [],
  };

  for (const request of queued) {
    try {
      // Make the API call
      await apiCall({
        url: request.url,
        method: request.method,
        data: request.data,
        headers: request.headers,
      });

      // Remove from queue on success
      await removeQueuedRequest(request.id);
      results.success++;
    } catch (error) {
      // Increment retry count
      request.retries = (request.retries || 0) + 1;

      // Remove if exceeded max retries (e.g., 5)
      if (request.retries >= 5) {
        await removeQueuedRequest(request.id);
        results.failed++;
        results.errors.push({
          url: request.url,
          error: error.message,
          retries: request.retries,
        });
      } else {
        // Update retry count in queue
        try {
          const database = await initDB();
          const transaction = database.transaction([STORE_NAME], 'readwrite');
          const store = transaction.objectStore(STORE_NAME);
          await store.put(request);
        } catch (dbError) {
          console.error('Error updating retry count:', dbError);
        }
        results.failed++;
      }
    }
  }

  return results;
}

/**
 * Get queue count
 * @returns {Promise<number>} Number of queued requests
 */
export async function getQueueCount() {
  const queued = await getQueuedRequests();
  return queued.length;
}

/**
 * Clear stale queued requests (older than specified hours)
 * @param {number} maxAgeHours - Maximum age in hours (default: 24)
 * @returns {Promise<number>} Number of requests cleared
 */
export async function clearStaleRequests(maxAgeHours = 24) {
  try {
    const queued = await getQueuedRequests();
    const now = Date.now();
    const maxAge = maxAgeHours * 60 * 60 * 1000; // Convert to milliseconds
    let cleared = 0;
    
    for (const request of queued) {
      const age = now - (request.timestamp || 0);
      if (age > maxAge) {
        await removeQueuedRequest(request.id);
        cleared++;
      }
    }
    
    return cleared;
  } catch (error) {
    console.error('Error clearing stale requests:', error);
    return 0;
  }
}







